// @hamecho/content-schema is imported as a relative path to local content-schema package
import { TagNodeSchema } from "@hamecho/content-schema";
import { $applyNodeReplacement, DecoratorNode, type LexicalNode, type NodeKey, type SerializedLexicalNode, Spread } from "lexical";
import { JSX } from "react";
import { NODE_TYPE_ENTITY } from "@/constants/lexical";
import { getTagById, getTagColorClassByTagId } from "@/services/TagService";

export type SerializedTagNode = Spread<
  {
    type: typeof NODE_TYPE_ENTITY.TAG;
    version: 1;
    key: string;
    tagId: string;
  },
  SerializedLexicalNode
>;

/**
 * TagNode - 标签实体节点（原子节点）
 *
 * 特性：
 * - DecoratorNode（装饰节点）：React 组件渲染
 * - 原子节点：不可分割，整体删除
 * - 用于 hashtag 标记
 *
 * Per architecture: Node holds value fields directly. Content Schema is SSOT via Adapter.
 * TagNode stores ONLY tagId (UUID). displayName and colorToken are looked up from TagService.
 */
export class TagNode extends DecoratorNode<JSX.Element> {
  __tagId: string;

  static getType(): string {
    return NODE_TYPE_ENTITY.TAG;
  }

  /**
   * Clone this node
   */
  static clone(node: TagNode): TagNode {
    return new TagNode(node.__tagId, node.__key);
  }

  /**
   * Create a new TagNode. tagId is required (UUID format).
   * Empty string "" means tag is not yet created (will be filled asynchronously).
   */
  constructor(tagId: string, key?: NodeKey) {
    super(key);
    this.__tagId = tagId;
  }

  // ========== 关键：原子节点行为声明 ==========

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  // ========== DOM 操作 ==========

  createDOM(): HTMLElement {
    return document.createElement("span");
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    // Look up tag by tagId (SSOT)
    const tagInfo = getTagById(this.__tagId);
    if (tagInfo) {
      const colorClass = getTagColorClassByTagId(this.__tagId);
      return (
        <span
          className={`inline-flex items-center px-1.5 py-0.5 rounded ${colorClass} hover:opacity-80 transition-opacity cursor-pointer select-none`}
        >
          #{tagInfo.displayName}
        </span>
      );
    }

    // Tag not found in cache (deleted or not loaded)
    return (
      <span
        className={`inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 hover:opacity-80 transition-opacity cursor-pointer select-none`}
      >
        #[unknown tag]
      </span>
    );
  }

  // ========== 序列化 ==========

  static importJSON(serializedNode: SerializedTagNode): TagNode {
    // Validate with schema
    const parsedData = {
      type: NODE_TYPE_ENTITY.TAG,
      version: 1,
      key: serializedNode.key,
      tagId: serializedNode.tagId,
    };

    TagNodeSchema.parse(parsedData);

    const node = $createTagNode(serializedNode.tagId);
    return node;
  }

  exportJSON(): SerializedTagNode {
    const json = {
      ...super.exportJSON(),
      type: NODE_TYPE_ENTITY.TAG,
      version: 1 as const,
      key: this.__key,
      tagId: this.__tagId,
    };
    return json;
  }

  // ========== Getter/Setter ==========

  getTagId(): string {
    return this.__tagId;
  }

  setTagId(tagId: string): void {
    const writable = this.getWritable();
    writable.__tagId = tagId;
  }

  // ========== 其他 ==========

  getTextContent(): string {
    const tagInfo = getTagById(this.__tagId);
    if (tagInfo) {
      return `#${tagInfo.displayName}`;
    }
    return `#[tag:${this.__tagId}]`;
  }

  isEqualTo(other: LexicalNode): boolean {
    if (!(other instanceof TagNode)) {
      return false;
    }
    return this.__tagId === other.__tagId;
  }
}

/**
 * 创建 TagNode
 *
 * @param tagId - Tag UUID. Can be empty string "" for temporary creation (will be filled later)
 */
export function $createTagNode(tagId: string): TagNode {
  if (tagId === null || tagId === undefined) {
    throw new Error("tagId is required to create TagNode");
  }
  // Allow empty string "" for async creation (will be filled in later)
  const tagNode = new TagNode(tagId);
  return $applyNodeReplacement(tagNode);
}

/**
 * 检查节点是否为 TagNode
 */
export function $isTagNode(node: LexicalNode | null | undefined): node is TagNode {
  return node instanceof TagNode;
}
